import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime, fmtBytes } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { FILE_CATEGORIES } from "@/lib/advertising";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Folder, Upload, Download, Trash2, BadgeCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function AdvertiserFiles({ businessId }) {
  const { user } = useAuth();
  const canWrite = user?.role !== "readonly";
  const canDelete = ["sales_manager", "tenant_owner", "platform_owner"].includes(user?.role);
  const [files, setFiles] = useState([]);
  const [cat, setCat] = useState("contracts");
  const [label, setLabel] = useState("");
  const [placements, setPlacements] = useState([]);
  const [pid, setPid] = useState("none");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = useCallback(() => api.get(`/files?related_type=businesses&related_id=${businessId}`).then((r) => setFiles(r.data.items)), [businessId]);
  useEffect(() => { load(); api.get(`/placements?business_id=${businessId}`).then((r) => setPlacements(r.data.items)).catch(() => {}); }, [load, businessId]);

  const inCat = (k) => files.filter((f) => (f.category || "misc") === k);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("related_type", "businesses"); fd.append("related_id", businessId);
    fd.append("category", cat); fd.append("version_label", label); if (pid !== "none") fd.append("placement_id", pid);
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const approve = async (f) => { try { await api.post(`/files/${f.id}/approve`); toast.success("Marked as current approved version"); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const remove = async (f) => { if (!window.confirm(`Remove ${f.original_filename}?`)) return; try { await api.delete(`/files/${f.id}`); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const zoneName = (id) => placements.find((p) => p.id === id)?.zone;
  const current = FILE_CATEGORIES.find((c) => c.key === cat);
  const list = inCat(cat);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm grid md:grid-cols-[230px_1fr]" data-testid="advertiser-files">
      <nav className="border-b md:border-b-0 md:border-r border-slate-100 p-2 space-y-0.5">
        {FILE_CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`files-cat-${c.key}`}
            className={cn("w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm", cat === c.key ? "bg-[#00C9E0]/10 text-[#0094A8] font-semibold" : "text-slate-600 hover:bg-slate-50")}>
            <Folder className="h-4 w-4 shrink-0" /><span className="truncate flex-1">{c.label}</span>
            <span className="text-[11px] tnum text-slate-400">{inCat(c.key).length}</span>
          </button>
        ))}
      </nav>
      <div className="p-4 min-w-0">
        <h3 className="font-display font-semibold text-slate-800">{current.label}</h3>
        <p className="text-xs text-slate-400">{current.hint}</p>
        {canWrite && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input className="h-8 w-56" placeholder="Version name, e.g. November Ad" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="files-version-label" />
            {placements.length > 0 && (
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger className="h-8 w-48" data-testid="files-placement"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">All zones</SelectItem>{placements.map((p) => <SelectItem key={p.id} value={p.id}>{p.zone}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <input ref={ref} type="file" className="hidden" onChange={upload} data-testid="files-input" />
            <Button size="sm" className="h-8" onClick={() => ref.current?.click()} disabled={busy} data-testid="files-upload">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload</Button>
          </div>
        )}
        {list.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No files in this folder yet.</p> : (
          <ul className="mt-3 divide-y divide-slate-100">
            {list.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`file-${f.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{f.version_label ? `${f.version_label} – Version ${f.version}` : f.original_filename}
                    {f.approved && <span className="ml-2 inline-flex items-center gap-1 rounded bg-[#9ACC32]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#4A700A]" data-testid={`file-approved-${f.id}`}><BadgeCheck className="h-3 w-3" />{cat === "print_ads" ? "Current approved ad" : "Current approved"}</span>}</p>
                  <p className="text-[11px] text-slate-400">{f.original_filename} · {fmtBytes(f.size)} · {f.uploaded_by_name} · {fmtDateTime(f.created_at)}{f.placement_id ? ` · ${zoneName(f.placement_id) || "zone"}` : ""}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {canWrite && !f.approved && ["print_ads", "proofs", "contracts", "website", "coupons"].includes(cat) && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => approve(f)} data-testid={`file-approve-${f.id}`}>Mark approved</Button>}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadBlob(`/files/${f.id}/download`, f.original_filename)} data-testid={`file-download-${f.id}`}><Download className="h-4 w-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => remove(f)} data-testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
